/**
 * Maps an ISO 3166-1 alpha-2 country code to its international calling/dialing code.
 * The merchant's location `country` (from the location-details API, e.g. "US") drives
 * the phone country code shown in catering forms.
 */
const CALLING_CODES: Record<string, string> = {
  US: '1', CA: '1',
  GB: '44', IE: '353',
  IN: '91', LK: '94', PK: '92', BD: '880', NP: '977',
  AU: '61', NZ: '64',
  AE: '971', SA: '966', QA: '974', KW: '965', BH: '973', OM: '968',
  SG: '65', MY: '60', ID: '62', PH: '63', TH: '66', VN: '84', HK: '852',
  CN: '86', JP: '81', KR: '82',
  DE: '49', FR: '33', ES: '34', IT: '39', NL: '31', BE: '32', CH: '41',
  AT: '43', SE: '46', NO: '47', DK: '45', FI: '358', PT: '351', PL: '48',
  RU: '7', TR: '90', GR: '30',
  ZA: '27', NG: '234', KE: '254', EG: '20',
  BR: '55', MX: '52', AR: '54', CL: '56', CO: '57',
};

/** Default calling code used when the merchant's country is unknown or missing (US-based portal). */
export const DEFAULT_CALLING_CODE = '+1';

/**
 * Returns the international calling code (e.g. "+1", "+91") for an ISO alpha-2 country code.
 * Falls back to {@link DEFAULT_CALLING_CODE} when the country is missing or unrecognised.
 */
export function getCallingCode(country?: string | null): string {
  if (!country) return DEFAULT_CALLING_CODE;
  const key = String(country).trim().toUpperCase();
  const code = CALLING_CODES[key];
  return code ? `+${code}` : DEFAULT_CALLING_CODE;
}

/**
 * Combines an already-resolved calling code (e.g. "+91") with a locally-formatted phone
 * number into the backend's expected `+<code>-<digits>` format (e.g. "+91-9876543210")
 * for submission.
 */
export function toE164FromCallingCode(localPhone: string, callingCode: string): string {
  const digits = localPhone.replace(/\D/g, '');
  return digits ? `${callingCode}-${digits}` : '';
}

/** Convenience wrapper: resolves the calling code from `country` before combining. */
export function toE164(localPhone: string, country?: string | null): string {
  return toE164FromCallingCode(localPhone, getCallingCode(country));
}

/**
 * Reverses {@link toE164FromCallingCode} for editing: strips a stored E.164 phone back
 * down to local digits so it can be re-punctuated by `formatUSPhone()` for display in a
 * form field. Falls back to stripping any other "+"-prefixed code (last 10 digits) or,
 * for legacy records saved before E.164 formatting was introduced, returns the value
 * unchanged (already local digits, no prefix to strip).
 */
export function stripCallingCode(phone: string, callingCode: string): string {
  const trimmed = (phone ?? '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith(callingCode)) return trimmed.slice(callingCode.length);
  if (trimmed.startsWith('+')) {
    const digits = trimmed.replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  }
  return trimmed;
}
